import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Archive, Trash2, ChevronUp, ChevronDown, Sparkles, Users2, Folder, FileText, Check, X, PackagePlus } from "lucide-react";
import EditTierDialog from "./EditTierDialog";
import FeatureRegistryDialog from "./FeatureRegistryDialog";
import AddonsSection from "./AddonsSection";
import { useConfirm } from "../../components/ConfirmProvider";

/**
 * Super-admin tab that manages the platform's subscription tier catalog.
 * Adds/edits/archives/reorders tiers; opens a side dialog to manage the
 * feature registry (the master list of feature flags any tier can toggle).
 *
 * Assignment of a tier to a specific ID firm happens on the "ID firms" tab
 * (`EditFirmDialog` opens a Subscription section) — this tab is the catalog
 * side only.
 */
export default function SubscriptionTiersTab() {
  const [tiers, setTiers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full tier doc or {} for new
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const confirm = useConfirm();

  const featureMap = useMemo(
    () => Object.fromEntries(features.map((f) => [f.key, f])),
    [features],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, f] = await Promise.all([
        api.get("/superadmin/subscription-tiers"),
        api.get("/superadmin/subscription-features"),
      ]);
      setTiers(t.data.tiers || []);
      setFeatures(f.data.features || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (tierId, dir) => {
    const idx = tiers.findIndex((t) => t.tier_id === tierId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= tiers.length) return;
    const reordered = [...tiers];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setTiers(reordered); // optimistic
    try {
      await api.post("/superadmin/subscription-tiers/reorder", {
        tier_ids: reordered.map((t) => t.tier_id),
      });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const archive = async (tier) => {
    const ok = await confirm({
      title: `Archive "${tier.name}"?`,
      message: "It will be hidden from new subscriptions. Firms currently on this tier must be reassigned first.",
      confirmText: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/superadmin/subscription-tiers/${tier.tier_id}/archive`);
      toast.success("Tier archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (tier) => {
    if (tier.slug === "trial") {
      toast.error("The Trial tier is the mandatory fallback and cannot be deleted.");
      return;
    }
    const assigned = tier.assigned_firm_count || 0;
    const message = assigned > 0
      ? `${assigned} firm(s) currently on this tier will be reassigned to the Trial tier. This cannot be undone.`
      : "This cannot be undone.";
    const ok = await confirm({
      title: `Delete "${tier.name}" permanently?`,
      message,
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/superadmin/subscription-tiers/${tier.tier_id}`);
      toast.success(
        data?.reassigned_firms > 0
          ? `Tier deleted. ${data.reassigned_firms} firm(s) reassigned to Trial.`
          : "Tier deleted"
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-[#7A7571]">Loading subscription tiers…</div>;

  return (
    <div className="space-y-6" data-testid="subscription-tiers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-2xl font-bold text-[#2C2A29]">Subscription tiers</div>
          <p className="text-sm text-[#7A7571] mt-1 max-w-2xl">
            Platform pricing catalog. Assigning a tier to a firm copies its current limits + features as a snapshot — future edits here don&apos;t retroactively touch live firms unless you re-apply.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="manage-features-btn"
            onClick={() => setFeatureDialogOpen(true)}
            className="btn-ghost inline-flex items-center gap-2"
          >
            <Sparkles size={14} /> Manage features
          </button>
          <button
            type="button"
            data-testid="add-tier-btn"
            onClick={() => setEditing({})}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Add tier
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {tiers.map((t, i) => (
          <TierCard
            key={t.tier_id}
            tier={t}
            featureMap={featureMap}
            onEdit={() => setEditing(t)}
            onArchive={() => archive(t)}
            onDelete={() => remove(t)}
            onMoveUp={i === 0 ? null : () => move(t.tier_id, -1)}
            onMoveDown={i === tiers.length - 1 ? null : () => move(t.tier_id, 1)}
          />
        ))}
      </div>

      {editing !== null && (
        <EditTierDialog
          initial={editing}
          features={features}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {featureDialogOpen && (
        <FeatureRegistryDialog
          features={features}
          onClose={() => setFeatureDialogOpen(false)}
          onSaved={() => load()}
        />
      )}

      {/* Add-ons — sits below the tier catalog. Manages the reference
          catalog of paid add-ons that firms can layer on top of their tier. */}
      <div className="pt-8 border-t border-[#EBE5D9]">
        <AddonsSection />
      </div>
    </div>
  );
}

function TierCard({ tier, featureMap, onEdit, onArchive, onDelete, onMoveUp, onMoveDown }) {
  const price = formatPrice(tier.currency, tier.price_monthly_cents);
  const enabled = (tier.features || []).filter((f) => f.enabled);

  return (
    <div className="card-soft flex flex-col" data-testid={`tier-card-${tier.slug}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">{tier.slug}</div>
          <div className="font-display text-xl font-bold text-[#2C2A29]">{tier.name}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {onMoveUp && (
            <button type="button" onClick={onMoveUp} data-testid={`tier-up-${tier.slug}`} className="text-[#7A7571] hover:text-[#2C2A29]" title="Move up"><ChevronUp size={14} /></button>
          )}
          {onMoveDown && (
            <button type="button" onClick={onMoveDown} data-testid={`tier-down-${tier.slug}`} className="text-[#7A7571] hover:text-[#2C2A29]" title="Move down"><ChevronDown size={14} /></button>
          )}
        </div>
      </div>

      <div className="text-2xl font-display font-bold text-[#E07A5F]">{price}</div>
      {tier.price_annually_cents != null && tier.price_annually_cents !== 0 && (
        <div className="text-xs text-[#7A7571] mt-0.5">
          or {formatPrice(tier.currency, tier.price_annually_cents)} / year
        </div>
      )}

      {tier.description && (
        <p className="text-sm text-[#7A7571] mt-3 leading-relaxed">{tier.description}</p>
      )}

      <div className="mt-4 space-y-1.5 text-sm">
        <LimitRow icon={<Users2 size={14} />} label="Team users" value={tier.limits?.max_team_users} />
        <LimitRow icon={<Folder size={14} />} label="Concurrent projects" value={tier.limits?.max_concurrent_projects} />
        <LimitRow icon={<Archive size={14} />} label="Archived projects" value={tier.limits?.max_archived_projects} />
        <LimitRow icon={<FileText size={14} />} label="Documents / project" value={tier.limits?.max_documents_per_project} />
      </div>

      {tier.default_trial_days > 0 && (
        <div className="mt-3 inline-flex self-start items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-[#E0F2FE] text-[#075985] border border-[#7DD3FC]">
          {tier.default_trial_days}-day trial
        </div>
      )}

      {enabled.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#EBE5D9]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1.5">Included features</div>
          <ul className="space-y-1">
            {enabled.map((f) => (
              <li key={f.key} className="text-xs text-[#2C2A29] flex items-center gap-1.5">
                <Check size={12} className="text-[#166534] flex-shrink-0" />
                {featureMap[f.key]?.label || f.key}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto pt-4 flex items-center justify-between text-xs text-[#7A7571]">
        <span>{tier.assigned_firm_count} firm{tier.assigned_firm_count === 1 ? "" : "s"} assigned</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onEdit}
            data-testid={`tier-edit-${tier.slug}`}
            className="inline-flex items-center gap-1 text-[#2C2A29] hover:underline"
          >
            <Edit2 size={12} /> Edit
          </button>
          {tier.assigned_firm_count === 0 && (
            <button
              type="button"
              onClick={onArchive}
              data-testid={`tier-archive-${tier.slug}`}
              className="inline-flex items-center gap-1 text-[#7A7571] hover:text-[#B85536]"
            >
              <Archive size={12} /> Archive
            </button>
          )}
          {tier.slug !== "trial" && (
            <button
              type="button"
              onClick={onDelete}
              data-testid={`tier-delete-${tier.slug}`}
              className="inline-flex items-center gap-1 text-[#7A7571] hover:text-[#9F1239]"
              title={tier.assigned_firm_count > 0 ? `${tier.assigned_firm_count} firm(s) will be reassigned to Trial` : "Delete permanently"}
            >
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LimitRow({ icon, label, value }) {
  const display = value == null ? "Unlimited" : Number(value).toLocaleString();
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#7A7571] inline-flex items-center gap-1.5">
        {icon} {label}
      </span>
      <span className="font-semibold text-[#2C2A29]">{display}</span>
    </div>
  );
}

export function formatPrice(currency, cents) {
  if (cents == null) return "Contact sales";
  if (cents === 0) return "Free";
  const amount = cents / 100;
  return `${currency || "SGD"} ${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/mo`;
}
